import { useEffect } from "react";

const getSelectedCourseNames = (selectedCourses, courseList) => {
  const selectedCourseIds = new Set(selectedCourses.map(Number));

  return new Set(
    courseList
      .filter((course) => selectedCourseIds.has(Number(course.id)))
      .map((course) => course.name?.trim().toLowerCase())
      .filter(Boolean)
  );
};

const getFolderCourseIds = (folder) => {
  const courseId = folder.course_id ?? folder.courseId ?? folder.course?.id;
  const courseIds = Array.isArray(folder.course_ids) ? folder.course_ids : [];

  return courseId != null ? [courseId, ...courseIds] : courseIds;
};

const getFolderChildren = (folder) => {
  if (Array.isArray(folder.children)) return folder.children;

  return Object.entries(folder.children || {}).map(([childKey, child]) => ({
    ...child,
    __folderKey: childKey,
  }));
};

const getMainFolderIds = (folderItems, selectedCourses, courseList, selectedFolderIds = []) => {
  const selectedCourseIds = new Set(selectedCourses.map(Number));
  const selectedCourseNames = getSelectedCourseNames(selectedCourses, courseList);
  const mainFolderIds = [];
  const selectedFolderIdSet = new Set(selectedFolderIds.map(Number));

  const hasSelectedDescendantForCourse = (folder, inheritedCourseId = null) => {
    return getFolderChildren(folder).some((child) => {
      const childCourseId =
        child.course_id ?? child.courseId ?? inheritedCourseId;
      const isSelected = selectedFolderIdSet.has(Number(child.id));
      const matchesCourse =
        getFolderCourseIds(child).some((id) => selectedCourseIds.has(Number(id))) ||
        (childCourseId != null && selectedCourseIds.has(Number(childCourseId)));

      return (
        (isSelected && matchesCourse) ||
        hasSelectedDescendantForCourse(child, childCourseId)
      );
    });
  };

  const visitFolder = (folder, key = null, inheritedCourseId = null, depth = 0) => {
    if (!folder || typeof folder !== "object") return;

    const isMainFolder = depth === 0;
    const folderCourseIds = getFolderCourseIds(folder);
    const folderCourseId =
      folder.course_id ?? folder.courseId ?? inheritedCourseId;
    const folderName = folder.name?.trim().toLowerCase();
    const matchesSelectedCourse =
      folderCourseIds.some((id) => selectedCourseIds.has(Number(id))) ||
      (folderCourseId != null && selectedCourseIds.has(Number(folderCourseId))) ||
      (!folderCourseIds.length && selectedCourseIds.has(Number(key))) ||
      (!folderCourseIds.length && selectedCourseNames.has(folderName));

    if (
      isMainFolder &&
      matchesSelectedCourse &&
      !hasSelectedDescendantForCourse(folder, folderCourseId)
    ) {
      mainFolderIds.push(folder.id);
    }

    getFolderChildren(folder).forEach((child) => {
      visitFolder(child, child.__folderKey, folderCourseId, depth + 1);
    });
  };

  const normalizedFolders =
    folderItems?.quiz_directories ||
    folderItems?.data?.quiz_directories ||
    folderItems?.data ||
    folderItems ||
    [];

  if (Array.isArray(normalizedFolders)) {
    normalizedFolders.forEach((folder) => visitFolder(folder));
  } else {
    Object.entries(normalizedFolders).forEach(([key, folder]) => {
      visitFolder(folder, key);
    });
  }

  return mainFolderIds.filter(Boolean);
};

export const useCourseFolderSelection = ({
  folderItems,
  selectedCourses = [],
  courseList = [],
  syncCourseFolderSelections,
  selectedFolderIds = [],
  latestSelectedCourseId = null,
}) => {
  useEffect(() => {
    const coursesToCheck =
      latestSelectedCourseId == null
        ? selectedCourses
        : [latestSelectedCourseId];
    const mainFolderIds = getMainFolderIds(
      folderItems,
      coursesToCheck,
      courseList,
      selectedFolderIds
    );

    syncCourseFolderSelections(mainFolderIds);
  }, [folderItems, selectedCourses, courseList, syncCourseFolderSelections, selectedFolderIds, latestSelectedCourseId]);
};
